import { useQuery } from "@tanstack/react-query";
import { useLanguage } from "../context/LanguageContext";

const OFFERS_API = "https://erp-elsalab.odoo.com/api/cs/offers";

async function fetchOffers(language = "en") {
  try {
    // Always send explicit lang param so backend returns correct locale
    let url = `${OFFERS_API}?lang=${language === "ar" ? "ar" : "en"}`;
    const response = await fetch(url);
    if (!response.ok) {
      console.error(
        `Failed to fetch offers: ${response.status} ${response.statusText}`
      );
      throw new Error(`Failed to fetch offers: ${response.status}`);
    }
    const payload = await response.json();
    const raw = payload?.data || [];

    // Normalize offers structure to a consistent shape usable by UI
    return raw.map((o) => ({
      id: o.id,
      name: o.name,
      promotionalText: o.promotional_text ?? null,
      promotionalTextType: o.promotional_text_font ?? null,
      description: o.description ?? null,
      imageUrl: o.image_url,
      imageUrlKey: o.image_url_key,
      shape: o.shape || null,
      backgroundColor: o.background_color || null,
      isBannerOffer: Boolean(o.is_banner_offer),
      isSpotlightOffer: Boolean(o.is_spotlight_offer),
      products: Array.isArray(o.products) ? o.products : [],
      relatedOffers: Array.isArray(o.related_offers)
        ? o.related_offers.map((ro) => ({
            id: ro.id,
            name: ro.name,
            promotionalText: ro.promotional_text ?? null,
            promotionalTextType: ro.promotional_text_font ?? null,
            description: ro.description ?? null,
            imageUrl: ro.image_url,
            imageUrlKey: ro.image_url_key,
            shape: ro.shape || null,
            backgroundColor: ro.background_color || null,
            isBannerOffer: Boolean(ro.is_banner_offer),
            isSpotlightOffer: Boolean(ro.is_spotlight_offer),
            products: Array.isArray(ro.products) ? ro.products : [],
          }))
        : [],
    }));
  } catch (error) {
    console.error("Error fetching offers:", error);
    // Log specific error types for debugging
    if (
      error.name === "TypeError" &&
      error.message.includes("Failed to fetch")
    ) {
      console.error(
        "Network error or CORS issue - check backend CORS configuration"
      );
    }
    throw error;
  }
}

export function useOffers() {
  const { language } = useLanguage();
  const query = useQuery({
    queryKey: ["offers", language],
    queryFn: () => fetchOffers(language),
    staleTime: 5 * 60 * 1000,
    retry: 2, // Retry failed requests 2 times
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  });

  const offers = query.data || [];
  const bannerOffers = offers.filter((o) => o.isBannerOffer);
  const spotlightOffers = offers.filter((o) => o.isSpotlightOffer);
  const productOffers = offers.filter(
    (o) => !o.isBannerOffer && !o.isSpotlightOffer
  );

  // Group spotlight offers by promotional text (fallback label like mobile)
  const spotlightOffersGrouped = spotlightOffers.reduce((acc, o) => {
    const key = o.promotionalText || "The Spotlight";
    if (!acc[key]) acc[key] = [];
    acc[key].push(o);
    return acc;
  }, {});

  return {
    offers,
    bannerOffers,
    spotlightOffers,
    spotlightOffersGrouped,
    productOffers,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    isFetching: query.isFetching,
  };
}
